import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, FormControl, Grid, useTheme, useMediaQuery } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import MoveButton from "../components/MoveButton";

import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import "../styles/filepond.css";
import { moveButtonSx } from "../lib/uiformatting";

registerPlugin(
    FilePondPluginImageExifOrientation,
    FilePondPluginImagePreview,
    FilePondPluginFileValidateType
);

export default function FileUpload(props) {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const [pond, setPond] = useState(null);
    const hasFetchedRemote = useRef(false);

    useEffect(() => {
        if (!props.uploadedUrls || props.uploadedUrls.length === 0) return;
        if (hasFetchedRemote.current) return;

        hasFetchedRemote.current = true;

        async function fetchAndConvert() {
            try {
                const fetchedFiles = await Promise.all(
                    props.uploadedUrls.map(async (url, index) => {
                        const response = await fetch(url);
                        if (!response.ok) {
                            throw new Error(`Failed to fetch image file: ${response.status}`);
                        }
                        const blob = await response.blob();
                        return new File([blob], `uploaded-photo-${index + 1}.jpg`, {
                            type: blob.type,
                        });
                    })
                );
                props.setFiles((prevFiles) => [...prevFiles, ...fetchedFiles]);
            } catch (error) {
                console.error("Error fetching image files:", error);
            }
        }

        fetchAndConvert();
    }, [props.uploadedUrls, props.setFiles]);

    const filePondItems = useMemo(() => {
        return (props.files || []).map((file) => ({
            source: file,
            options: { type: "local" },
        }));
    }, [props.files]);

    const onUpdateFiles = (fileItems) => {
        const newFiles = fileItems.map((item) => item.file);
        props.setFiles(newFiles);
    };

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: isDesktop ? "600px" : "100%",
                margin: "0 auto",
                ...props.sx,
            }}
        >
            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={6}>
                    <MoveButton
                        title="Upload Photo"
                        onClick={() => {
                            // TODO: debug hacky fix in the future (useState, useEffect with captureMethod prop not working)
                            document.querySelector(".filepond--browser")?.removeAttribute("capture");
                            pond?.browse();
                        }}
                        sx={{ ...moveButtonSx }}
                        IconComponent={PhotoLibraryIcon}
                    />
                </Grid>
                <Grid item xs={6}>
                    <MoveButton
                        title="Take Photo"
                        onClick={() => {
                            document.querySelector(".filepond--browser")?.setAttribute("capture", "environment");
                            pond?.browse();
                        }}
                        sx={{ ...moveButtonSx }}
                        IconComponent={CameraAltIcon}
                    />
                </Grid>
            </Grid>

            <FormControl sx={{ width: "100%", mt: "0.75rem" }}>
                <FilePond
                    ref={(ref) => {
                        if (ref) {
                            props.setPond?.(ref);
                            setPond(ref);
                        }
                    }}
                    files={filePondItems}
                    onupdatefiles={onUpdateFiles}
                    allowMultiple={props.multiple}
                    instantUpload={false}
                    // captureMethod={capture}
                    allowProcess={false}
                    storeAsFile={true}
                    labelIdle=""
                    acceptedFileTypes={props.acceptedTypes ?? ["image/*", "image/heic", "image/heif"]}
                    credits={false} // remove filepond credits - I think the UI looks better w/o it, but it might be morally questionable to do considering filepond is open source
                />
            </FormControl>
        </Box>
    );
}
